import { ExclamationCircleOutlined } from "@ant-design/icons";
import { Button, Modal, Popconfirm } from "antd";
import { useContext, useEffect, useState } from "react";
import { HomeContext } from "../../containers/Home";
import useFormHandler from "../../hooks/useFormHandler";
import { getRolePermissionList } from "../../services/api-server/general_roles";
import { updateProjectItem } from "../../services/api-server/projects";
import { getWellProgrammeTemplateList } from "../../services/api-server/well_programme_template";
import { useProjectStore } from "../../stores/projectStore";
import { TProjectData } from "../../types/project";
import { alertMessage } from "../../utils/alert";
import { GetAntIcon } from "../../utils/ant_icons";
import CustomForm from "../CustomForm";
import FormButtonSave from "../FormButtonSave";

interface RACIModalProps {
	open: boolean;
	setOpen: (open: boolean) => void;
	template: any;
	extraData?: any;
	operationFormRef?: any;
	extraSaveFunction?: () => void;
}

function RACIModal(props: RACIModalProps) {
	const { formRef, setFormReset, formReset, resetForm, initialForm } =
		useFormHandler();
	const { currentTenantKey, handleFormUpdating, userInfo }: any =
		useContext(HomeContext);

	const [templateData, setTemplateData] = useState<any>(null);

	const { project, setProject } = useProjectStore();

	const [cancelPopover, setCancelPopover] = useState<boolean>(false);

	const fetchData = async () => {
		return Promise.all([
			getWellProgrammeTemplateList(currentTenantKey, { _id: props.template }),
			getRolePermissionList(currentTenantKey),
		]).catch((err: any) => {
			console.log("err", err);
		});
	};

	useEffect(() => {
		fetchData()
			.then(([templateList, rolePermissionList]: any) => {
				const _templateData = templateList[0];
				setTemplateData(_templateData);

				const raci = Array.from(
					new Set(
						[
							"responsible_roles",
							"accountable_roles",
							"consulted",
							"informed_parties",
						]
							.flatMap((roleType) =>
								_templateData?.phases?.flatMap((phase: any) =>
									phase?.tasks?.map((task: any) => task?.[roleType])
								)
							)
							.flat()
							.filter(Boolean)
							.filter(
								(role: any) =>
									!project?.role_assignments?.some(
										(ra: any) => ra.role_id === role
									)
							)
					)
				);
				if (raci.length > 0) {
					const filteredDataOptions = raci.map((role: any) => {
						const roleData = rolePermissionList.find(
							(rp: any) => rp._id === role
						);
						return {
							role_id: roleData?._id,
							role: roleData?.name,
							appointee:
								project?.role_assignments?.find(
									(ra: any) => ra.role_id === roleData?._id
								)?.appointee || [],
						};
					});

					resetForm({ role_assignments: [...filteredDataOptions] });
				} else {
					props.setOpen(false);
					if (props.extraSaveFunction) {
						props.extraSaveFunction();
					}
				}
			})
			.catch((err: any) => {
				console.log("err", err);
			});
	}, [props.template]);

	const handleFormCancel = () => {
		props.setOpen(false);
		handleFormUpdating(false, project?._id);
		setCancelPopover(false);
		resetForm();
		props.operationFormRef?.setFieldValue("project_template", null);
	};

	const handleFormSave = async (values: any) => {
		try {
			props.setOpen(false);
			if (props.extraSaveFunction) {
				try {
					await props.extraSaveFunction();
				} catch (err) {
					console.log("extraSaveFunction failed child", err);
					handleFormUpdating(false, project?._id);
					return; // stop here, don't update project
				}
			}
			const newValues = {
				...values,
				role_assignments: [
					...(project?.role_assignments || []),
					...(values?.role_assignments || []),
				],
			};
			const updatedProject = await updateProjectItem(
				currentTenantKey,
				project?.project_code,
				newValues,
				initialForm,
				userInfo.user,
				["role_assignments"]
			);
			setProject(updatedProject as TProjectData);
			handleFormUpdating(false, project?._id);
		} catch (error) {
			alertMessage("error", "Changes are not saved. Please try again.");
		}
	};

	return (
		<Modal
			title={`New roles added from template ( ${templateData?.name ?? ""} )`}
			open={props.open}
			onCancel={() => props.setOpen(false)}
			closable={false}
			destroyOnClose
			centered
			maskClosable={false}
			footer={() => {
				return (
					<>
						<Popconfirm
							icon={<ExclamationCircleOutlined style={{ color: "grey" }} />}
							title={
								<div style={{ maxWidth: "300px", padding: "10px" }}>
									You have unsaved changes. Discard them or continue editing?
								</div>
							}
							open={cancelPopover}
							placement="topRight"
							cancelButtonProps={{ ghost: true }}
							cancelText="Discard changes"
							onCancel={handleFormCancel}
							okText="Continue editing"
							okType="default"
							onConfirm={() => {
								setCancelPopover(false);
							}}
						>
							{/* Cancel Button */}
							<Button
								className="ant-btn-secondary"
								onClick={() => {
									if (formReset) {
										handleFormCancel();
									} else {
										setCancelPopover(true);
									}
								}}
							>
								Cancel
							</Button>
						</Popconfirm>
						<FormButtonSave
							activeKey={project?._id}
							form={formRef}
							handleFormSave={handleFormSave}
							formReset={formReset}
							setFormReset={setFormReset}
						/>
					</>
				);
			}}
		>
			<div
				style={{
					display: "flex",
					flexDirection: "column",
					alignSelf: "stretch",
					gap: "20px",
					height: "50vh",
				}}
			>
				<span>
					The selected template contains roles that are not currently included
					in this project. These roles will be added automatically.
				</span>
				<span>
					{GetAntIcon("infocircle")} Please assign team members to these roles
					before proceeding. You can still update them in Project settings.
				</span>
				<CustomForm
					setFormReset={setFormReset}
					tabKey={"raci-modal"}
					formRef={formRef}
					dataOptions={props.extraData}
				/>
			</div>
		</Modal>
	);
}

export default RACIModal;
